import { TRUST_SCORE_SERVICE_OFFERING_ID } from "@constants/verification-service-details-constants";
import { AssertionScoreStatus } from "@enums/assertion-score-status-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { formatScoreModifiers } from "@formatters/rp-services/rp-service-configuration-score-formatter";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";
import { AssertionScoreAssertionStatusValueMap, AvailableAssertionStatus, AvailableScoreValue, TrustScoreAssertionStatusValueMap } from "@idw-types/data/reference-data/generic";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import {
    FormattedRpServiceConfigurationScoreModifier,
    RpServiceConfigurationScoreModifier
} from "@idw-types/data/rp-service-configuration/rp-service-configuration-score";
import { ScoreStatusTypeMap } from "@idw-types/data/rp-service-configuration/scores/score-status-map";
import { ScoreStatusType } from "@idw-types/data/rp-service-configuration/scores/score-status-type";
import { isNullOrUndefined } from "@utils/object-util";
import { safelyParseIntFromString } from "@utils/string-util";

export const isAssertionScoreEnabled = (
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
    assertionScoreServiceOfferingIDs: Array<string>
) => {
    return serviceOfferings.some(
        (offering) =>
            offering.serviceOfferingID &&
            offering.status === EntityStatusTypes.ACTIVE &&
            offering.serviceOfferingStatus === EntityStatusTypes.ACTIVE &&
            assertionScoreServiceOfferingIDs.includes(offering.serviceOfferingID)
    );
};

export const isTrustScoreEnabled = (
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
    trustScoreServiceOfferingIDs: Array<string>
) => {
    return serviceOfferings.some(
        (offering) =>
            offering.serviceOfferingID &&
            offering.status === EntityStatusTypes.ACTIVE &&
            offering.serviceOfferingStatus === EntityStatusTypes.ACTIVE &&
            trustScoreServiceOfferingIDs.includes(offering.serviceOfferingID)
    );
};
export const getActiveAssertionStatus = (
    validServiceOfferingIDs: Array<string>,
    validAPAssertionIDs: Array<string>,
    assertionStatusMaps: Array<ApAssertionStatusMap>
) => {
    return assertionStatusMaps.filter(
        (assertionStatusMap) =>
            validServiceOfferingIDs.includes(
                `${assertionStatusMap.serviceOfferingID}`
            ) && validAPAssertionIDs.includes(assertionStatusMap.apAssertionID)
    );
};

export const getValidApAssertionIDS = (
    serviceOfferings: FormattedRpServiceConfigurationServiceOffering[]
) => {
    return serviceOfferings.reduce((assertionIDS, serviceOffering) => {
        const { assertions } = serviceOffering;

        assertionIDS = [
            ...assertionIDS,
            ...assertions.map((assertion) => assertion.apAssertionId)
        ];
        return assertionIDS;
    }, []);
};

export const getActiveServiceOfferings = (
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>
) => {
    return serviceOfferings.filter(
        (serviceOffering) =>
            serviceOffering.serviceOfferingStatus === EntityStatusTypes.ACTIVE &&
            serviceOffering.status === EntityStatusTypes.ACTIVE &&
            serviceOffering.addedToService
    );
};

export const getValidServiceOfferingIDS = (
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>
) => {
    return getActiveServiceOfferings(serviceOfferings).map(
        (serviceOffering) => serviceOffering.serviceOfferingID
    );
};

export const getAssertionStatusIDS = (
    assertionStatuses: Array<ApAssertionStatusMap>,
    assertionID: string
): Array<string> => {
    return assertionStatuses.reduce((assertionStatusIDs, assertionStatus) => {
        if (assertionStatus.assertionID === assertionID) {
            assertionStatusIDs = assertionStatus.assertionStatusIDs;
        }
        return assertionStatusIDs;
    }, [] as Array<string>);
};

export const mapAssertionScoreModifiersByID = (
    assertionScoreModifiers: Array<RpServiceConfigurationScoreModifier>
) => {
    return assertionScoreModifiers.reduce((result, assertionScoreModifier) => {
        result[assertionScoreModifier.assertionID] = assertionScoreModifier;
        return result;
    }, {} as { [key: string]: RpServiceConfigurationScoreModifier });
};

export const getAssertionScoreModifiers = (
    formattedAssertionScoreModifiers: FormattedRpServiceConfigurationScoreModifier[]
): Array<RpServiceConfigurationScoreModifier> => {
    return formattedAssertionScoreModifiers.map(
        (formattedAssertionScoreModifier) => {
            const {
                id,
                assertionID,
                passScore,
                closeScore,
                partialScore,
                unverifiedScore,
                failScore,
                warnScore
            } = formattedAssertionScoreModifier;
            return {
                id,
                assertionID,
                passScore,
                closeScore,
                partialScore,
                unverifiedScore,
                failScore,
                warnScore
            };
        }
    );
};

const getNewServiceOfferings = (
    isScoreEnabled: boolean,
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
    scoreServiceOfferingIDs: Array<string>
) => {
    return serviceOfferings.map((so) => {
        if (scoreServiceOfferingIDs.includes(so.serviceOfferingID)) {
            if (!isScoreEnabled && !so.id) {
                return {
                    ...so,
                    order: !isNullOrUndefined(so.order) ? so.order : 0,
                    fallbackStatus: "primary",
                    status: EntityStatusTypes.INACTIVE,
                    addedToService: false
                };
            } else if (!isScoreEnabled) {
                return {
                    ...so,
                    order: !isNullOrUndefined(so.order) ? so.order : 0,
                    fallbackStatus: "primary",
                    status: EntityStatusTypes.INACTIVE,
                    addedToService: true
                };
            } else {
                return {
                    ...so,
                    order: !isNullOrUndefined(so.order) ? so.order : 0,
                    fallbackStatus: "primary",
                    status: EntityStatusTypes.ACTIVE,
                    addedToService: true
                };
            }

        } else {
            return so;
        }
    });
}

export const updateServiceOfferingsAndAssertionScoreModifiers = (
    { serviceOfferings }: FormattedRpServiceConfiguration,
    assertionStatusMaps: Array<ApAssertionStatusMap>,
    assertionScoreServiceOfferingIDs: Array<string>,
    scoreServiceOfferingIDs: Array<string>,
    isAssertionScoreEnabled: boolean
) => {
    let assertionScoreModifiers: FormattedRpServiceConfigurationScoreModifier[] = [];
    if (isAssertionScoreEnabled) {
        assertionScoreModifiers = formatScoreModifiers(
            [],
            serviceOfferings,
            assertionStatusMaps
        );
    }

    let newServiceOfferings = getNewServiceOfferings(isAssertionScoreEnabled, serviceOfferings, assertionScoreServiceOfferingIDs);

    newServiceOfferings = newServiceOfferings.map(so => {
        if (so.serviceOfferingID === TRUST_SCORE_SERVICE_OFFERING_ID) {
            // if the trust score service offering is active,
            //  we need to check to see if there are any service offerings active that the Trust Score SO needs
            // if not, we need to inactive the trust score service offering
            if (so.addedToService && so.status === EntityStatusTypes.ACTIVE) {
                const shouldTrustScoreBeEnabled = newServiceOfferings.some(newSO =>
                    scoreServiceOfferingIDs.includes(newSO.serviceOfferingID) &&
                    newSO.addedToService &&
                    newSO.status === EntityStatusTypes.ACTIVE
                );
                return {
                    ...so,
                    order: !isNullOrUndefined(so.order) ? so.order : 0,
                    fallbackStatus: "primary",
                    status: !shouldTrustScoreBeEnabled ? EntityStatusTypes.INACTIVE : so.status,
                    addedToService: !shouldTrustScoreBeEnabled && so.id === null ? false : so.addedToService
                }
            }
        }
        return so;
    });

    return { serviceOfferings: newServiceOfferings, assertionScoreModifiers };
};

export const updateServiceOfferingsAndTrustScoreModifiers = (
    { serviceOfferings }: FormattedRpServiceConfiguration,
    assertionStatusMaps: Array<ApAssertionStatusMap>,
    trustScoreServiceOfferingIDs: Array<string>,
    isTrustScoreEnabled: boolean
) => {
    let trustScoreModifiers: FormattedRpServiceConfigurationScoreModifier[] = [];
    if (isTrustScoreEnabled) {
        trustScoreModifiers = formatScoreModifiers(
            [],
            serviceOfferings,
            assertionStatusMaps
        );
    }

    const newServiceOfferings = getNewServiceOfferings(isTrustScoreEnabled, serviceOfferings, trustScoreServiceOfferingIDs);
    return { serviceOfferings: newServiceOfferings, trustScoreModifiers };
};

export const getScoreOptions = (
    scoreStatusType: ScoreStatusType,
    assertionStatusMap: ScoreStatusTypeMap
) => {
    const formattedAssertionStatus = assertionStatusMap[scoreStatusType];
    return formattedAssertionStatus.scoreValues;
};

export const hasScoreStatus = (
    scoreStatusType: ScoreStatusType,
    assertionStatusMap: ScoreStatusTypeMap,
    modifierStateAssertionStatusIDs: Array<string>
) => {
    const formattedAssertionStatus = assertionStatusMap[scoreStatusType];
    return (
        modifierStateAssertionStatusIDs !== undefined &&
        modifierStateAssertionStatusIDs.indexOf(formattedAssertionStatus.assertionStatus.id) > -1
    );
};

export const getAvailableScoreValuesByID = (
    availableScoreValues: Array<AvailableScoreValue>,
    trustScoreValueID: number
) => {
    return availableScoreValues.find(
        (scoreValue) => safelyParseIntFromString(scoreValue.id) === trustScoreValueID
    );
};

export const getScoreValues = (
    trustScoreValueIDs: Array<number>,
    availableAssertionScoreValues: Array<AvailableScoreValue>
) => {
    const trustScoreValues: Array<AvailableScoreValue> = [];

    trustScoreValueIDs.forEach((trustScoreValueID) => {
        const trustScoreValue = getAvailableScoreValuesByID(
            availableAssertionScoreValues,
            trustScoreValueID
        );
        trustScoreValues.push(trustScoreValue);
    });

    return trustScoreValues;
};

export const getAvailableAssertionStatusBy = (
    availableAssertionStatus: AvailableAssertionStatus,
    currentAssertionStatusType: ScoreStatusType,
    availableScoreValues: Array<AvailableScoreValue>,
    scoreAssertionStatusValueMaps: Array<TrustScoreAssertionStatusValueMap | AssertionScoreAssertionStatusValueMap>
) => {
    if (availableAssertionStatus.name === currentAssertionStatusType) {
        let scoreValueIDs: Array<number> = [];
        const statusValues = scoreAssertionStatusValueMaps.filter(valueMap => valueMap.availableAssertionStatusID === availableAssertionStatus.id);

        if (statusValues.length > 0) {
            if (Object.keys(statusValues[0]).includes("availableAssertionScoreValueID")) {
                scoreValueIDs = (statusValues as Array<AssertionScoreAssertionStatusValueMap>).map(sv => safelyParseIntFromString(sv.availableAssertionScoreValueID));
            } else if (Object.keys(statusValues[0]).includes("availableTrustScoreValueID")) {
                scoreValueIDs = (statusValues as Array<TrustScoreAssertionStatusValueMap>).map(sv => safelyParseIntFromString(sv.availableTrustScoreValueID));                
            }
        }

        const scoreValues = getScoreValues(
            scoreValueIDs,
            availableScoreValues
        );

        return {
            [currentAssertionStatusType]: {
                assertionStatus: availableAssertionStatus,
                scoreValueIDs,
                scoreValues
            },
        };
    }

    return {} as any;
};

export const getAvailableAssertionStatusMap = (
    availableAssertionStatuses: Array<AvailableAssertionStatus>,
    availableScoreValues: Array<AvailableScoreValue>,
    scoreAssertionStatusValueMaps: Array<AssertionScoreAssertionStatusValueMap | TrustScoreAssertionStatusValueMap>

): ScoreStatusTypeMap => {
    return availableAssertionStatuses.reduce(
        (result, availableAssertionStatus) => {
            result = {
                ...result,
                ...getAvailableAssertionStatusBy(
                    availableAssertionStatus,
                    AssertionScoreStatus.PASS,
                    availableScoreValues,
                    scoreAssertionStatusValueMaps
                ),
                ...getAvailableAssertionStatusBy(
                    availableAssertionStatus,
                    AssertionScoreStatus.CLOSE,
                    availableScoreValues,
                    scoreAssertionStatusValueMaps
                ),
                ...getAvailableAssertionStatusBy(
                    availableAssertionStatus,
                    AssertionScoreStatus.PARTIAL,
                    availableScoreValues,
                    scoreAssertionStatusValueMaps
                ),
                ...getAvailableAssertionStatusBy(
                    availableAssertionStatus,
                    AssertionScoreStatus.UNVERIFIED,
                    availableScoreValues,
                    scoreAssertionStatusValueMaps
                ),
                ...getAvailableAssertionStatusBy(
                    availableAssertionStatus,
                    AssertionScoreStatus.FAIL,
                    availableScoreValues,
                    scoreAssertionStatusValueMaps
                ),
                ...getAvailableAssertionStatusBy(
                    availableAssertionStatus,
                    AssertionScoreStatus.WARN,
                    availableScoreValues,
                    scoreAssertionStatusValueMaps
                ),
            };

            return result;
        },
        {} as ScoreStatusTypeMap
    );
};
